import {
  IPermitTypeFieldDefaultOptions,
  IPermitTypeFieldDefaultOptionsInput,
  IWorkflowsFilterInput,
  SitePermitStatus,
  SortDirection,
  WorkflowsFilterType,
} from '~/client/graph'

import {
  getFilterTypeFromUnassignedId,
  isUnassignedId,
} from '../components/WorkflowDirectory/components/WorkflowFilters/useWorkflowFilterCategories'
import {
  WorkflowGroupingKey,
  WorkflowSortingKey,
} from '../components/WorkflowList'
import {
  FILTER_TYPE_TO_WORKFLOWS_FILTER_TYPE,
  TAG_TYPE_TO_WORKFLOWS_FILTER_TYPE,
} from '../constants/FilterTypeMappings'
import SortOrder from '../enums/SortOrder'
import { TagType } from '../enums/TagType'
import WorkflowType from '../models/WorkflowType'

interface ILocalDefaults {
  sortKey: WorkflowSortingKey | null
  sortOrder: SortOrder
  groupByKey: WorkflowGroupingKey | null
  filterTagIds: string[]
}

interface ITagsStoreLike {
  getTagById: (id: string) => { type: TagType } | null
}

interface IProjectsStoreLike {
  list: { id?: string }[]
}

interface IPermitTypesStoreLike {
  getActualTypeByType: (type: string) => WorkflowType
  getPermitTypeById: (id: string) => WorkflowType
}

/**
 * Convert frontend model to GraphQL input for saving.
 * Groups tag IDs by their filter type and stores with the new IWorkflowsFilter format.
 */
export function toDefaultOptionsInput(
  local: ILocalDefaults,
  tagsStore: ITagsStoreLike,
  projectsStore?: IProjectsStoreLike,
  workflowTypesStore?: IPermitTypesStoreLike,
): IPermitTypeFieldDefaultOptionsInput {
  // Group filter tag IDs by their WorkflowsFilterType
  const filtersByType: Record<WorkflowsFilterType, string[]> = {} as Record<
    WorkflowsFilterType,
    string[]
  >

  // Form status IDs (SitePermitStatus enum values) - not in tagsStore
  const formStatusValues = Object.values(SitePermitStatus) as string[]

  // Build set of project IDs for O(1) lookup
  const projectIdSet = new Set(
    projectsStore?.list.map(p => p.id).filter(Boolean) || [],
  )

  for (const tagId of local.filterTagIds) {
    let filterType: WorkflowsFilterType | undefined
    let filterValue = tagId

    // Handle Unassigned IDs (e.g., 'Unassigned_location')
    if (isUnassignedId(tagId)) {
      const filterTypeFromId = getFilterTypeFromUnassignedId(tagId)
      if (filterTypeFromId) {
        filterType = FILTER_TYPE_TO_WORKFLOWS_FILTER_TYPE[filterTypeFromId]
      }
    }
    // Handle synthetic file type tags (prefixed with 'filetype_')
    else if (tagId.startsWith('filetype_')) {
      filterType = WorkflowsFilterType.Attachment
    }
    // Handle form status tags (SitePermitStatus enum values)
    else if (formStatusValues.includes(tagId)) {
      filterType = WorkflowsFilterType.Status
    }
    // Handle project IDs (not in tagsStore, use projectsStore)
    else if (projectIdSet.has(tagId)) {
      filterType = WorkflowsFilterType.Project
    }
    // Look up tag to get its type
    else {
      const tag = tagsStore.getTagById(tagId)
      if (!tag) continue

      filterType = TAG_TYPE_TO_WORKFLOWS_FILTER_TYPE[tag.type]
    }

    if (!filterType) continue

    if (filterType === WorkflowsFilterType.Type && workflowTypesStore) {
      const permitType = workflowTypesStore.getPermitTypeById(tagId)
      if (!permitType?.type) continue
      filterValue = permitType.type
    }

    if (!filtersByType[filterType]) {
      filtersByType[filterType] = []
    }
    filtersByType[filterType].push(filterValue)
  }

  // Convert grouped filters to IWorkflowsFilterInput array
  const filterOptions: IWorkflowsFilterInput[] = Object.entries(
    filtersByType,
  ).map(([type, values]) => ({
    type: type as WorkflowsFilterType,
    values,
  }))

  return {
    sortOption: local.sortKey ?? undefined,
    sortDirection: local.sortKey
      ? local.sortOrder === SortOrder.ASC
        ? SortDirection.Ascending
        : SortDirection.Descending
      : undefined,
    groupOption: local.groupByKey ?? undefined,
    filterOptions: filterOptions.length > 0 ? filterOptions : undefined,
  }
}

/**
 * Convert GraphQL response to frontend model for display.
 * Extracts all tag IDs from filterOptions regardless of filter type.
 */
export function fromDefaultOptions(
  options: IPermitTypeFieldDefaultOptions | null | undefined,
  workflowTypesStore?: IPermitTypesStoreLike,
): ILocalDefaults {
  const filterTagIds =
    options?.filterOptions?.flatMap(filterOption => {
      if (!filterOption?.values?.length) {
        return []
      }

      if (
        filterOption.type === WorkflowsFilterType.Type &&
        workflowTypesStore
      ) {
        return filterOption.values
          .map(type => workflowTypesStore.getActualTypeByType(type))
          .filter(Boolean)
          .map(type => type.id)
      }

      return filterOption.values.filter(Boolean)
    }) ?? []

  return {
    sortKey: (options?.sortOption as WorkflowSortingKey) ?? null,
    sortOrder:
      options?.sortDirection === SortDirection.Descending
        ? SortOrder.DESC
        : SortOrder.ASC,
    groupByKey: (options?.groupOption as WorkflowGroupingKey) ?? null,
    filterTagIds,
  }
}
